import { useState } from "react";
import { toast } from "sonner";
import { Database, RefreshCw, Trash2, Download, FileSpreadsheet, Info } from "lucide-react";
import { PageHeader, Panel, Empty, Loading, Chip, useFetch } from "@/components/common";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import ImportWidget from "@/components/ImportWidget";
import { useAuth } from "@/context/AuthContext";
import { api, download, errMsg, fDate } from "@/lib/api";

const KIND = {
  mooncard: "Mooncard", releves: "Relevés kilométriques", vehicules: "Véhicules", controles: "Contrôles",
  affectations: "Affectations", utilisateurs_chauffeur: "Chauffeurs", utilisateurs_commercial: "Commerciaux", entretiens: "Entretiens",
};

function ColumnsTable({ spec }) {
  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="tbl w-full" data-testid={`columns-${spec.kind}`}>
        <thead><tr><th>Colonne</th><th>Obligatoire</th><th>Description</th><th>Exemple</th></tr></thead>
        <tbody>
          {spec.columns.map((c) => (
            <tr key={c.header}>
              <td className="font-semibold text-[#0E2D43]">{c.header}</td>
              <td>{c.required ? <Chip cls="bg-[#FEF3E2] text-[#9a5a05] border-[#F49619]/50">Oui</Chip> : <span className="text-slate-400 text-xs">Non</span>}</td>
              <td className="whitespace-normal text-slate-600 min-w-[260px]">{c.description}</td>
              <td className="font-mono text-xs text-slate-500">{c.example || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImportStep({ spec, step, onDone }) {
  const dl = (fmt) => download(`/imports/template/${spec.kind}`, { fmt }, `modele-${spec.kind}.${fmt}`).catch(() => toast.error("Téléchargement impossible"));
  return (
    <div className="space-y-4" data-testid={`import-step-${spec.kind}`}>
      <Panel title={`${step}. ${spec.label}`} action={
        <div className="flex gap-2">
          <button onClick={() => dl("xlsx")} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 text-xs font-semibold hover:border-[#0E2D43]" data-testid={`template-xlsx-${spec.kind}`}><Download className="w-3.5 h-3.5" /> Modèle Excel</button>
          <button onClick={() => dl("csv")} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 text-xs font-semibold hover:border-[#0E2D43]" data-testid={`template-csv-${spec.kind}`}><Download className="w-3.5 h-3.5" /> Modèle CSV</button>
        </div>
      }>
        <div className="flex items-start gap-2 text-sm text-slate-600 mb-4"><Info className="w-4 h-4 text-[#F49619] shrink-0 mt-0.5" /><p>{spec.note}</p></div>
        <ColumnsTable spec={spec} />
      </Panel>
      <ImportWidget key={spec.kind} endpoint={spec.endpoint} params={spec.params} title="Importer le fichier"
        hint="Étape 1 : Prévisualiser (aucune donnée enregistrée). Étape 2 : Importer les lignes valides. Les données importées sont des données réelles (non DÉMO)."
        onDone={onDone} testid={`import-${spec.kind}`} />
    </div>
  );
}

function DemoPanel({ sum }) {
  const { demo, refresh } = useAuth();
  const [confirm, setConfirm] = useState(null);
  const [typed, setTyped] = useState("");
  const total = (sum.data || []).reduce((s, r) => s + r.demo, 0);
  const run = async () => {
    try {
      if (confirm === "reset") await api.post("/demo/reset");
      else await api.delete("/demo/all");
      toast.success(confirm === "reset" ? "Données de démonstration régénérées" : "Toutes les données DÉMO ont été supprimées");
      await refresh();
      sum.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setConfirm(null);
    setTyped("");
  };
  return (
    <Panel title="Données de démonstration" testid="demo-panel">
      <div className="flex items-start gap-3 text-sm text-slate-600 mb-4">
        <Database className="w-5 h-5 text-[#F49619] shrink-0" />
        <p>Les données importées sont réelles. La suppression retire uniquement les éléments marqués DÉMO, en une seule opération.</p>
      </div>
      {sum.loading ? <Loading /> : (
        <table className="tbl w-full mb-4" data-testid="demo-summary-table">
          <thead><tr><th>Données</th><th className="text-right">DÉMO</th><th className="text-right">Réelles</th></tr></thead>
          <tbody>{(sum.data || []).map((r) => (
            <tr key={r.collection} data-testid={`demo-summary-${r.collection}`}><td>{r.label}</td><td className="text-right tabular-nums">{r.demo}</td><td className="text-right tabular-nums font-semibold text-[#0E2D43]">{r.real}</td></tr>
          ))}</tbody>
        </table>
      )}
      <div className="flex flex-wrap gap-2">
        {total > 0 && <button onClick={() => setConfirm("delete")} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold" data-testid="demo-delete-all-button"><Trash2 className="w-4 h-4" /> Supprimer toutes les données DÉMO ({total})</button>}
        {!demo && <button onClick={() => setConfirm("reset")} className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 text-sm font-semibold hover:border-[#0E2D43]" data-testid="demo-reset-button"><RefreshCw className="w-4 h-4" /> Recharger la démo</button>}
      </div>
      <AlertDialog open={!!confirm} onOpenChange={() => { setConfirm(null); setTyped(""); }}>
        <AlertDialogContent data-testid="demo-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "reset" ? "Recharger les données de démonstration ?" : "Supprimer toutes les données DÉMO ?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "reset" ? "Des données fictives marquées DÉMO seront ajoutées." : `${total} éléments marqués DÉMO seront définitivement supprimés. Les données réelles ne sont pas touchées. Tapez SUPPRIMER pour confirmer.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirm === "delete" && <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="SUPPRIMER" data-testid="demo-confirm-input" />}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="demo-confirm-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={run} disabled={confirm === "delete" && typed !== "SUPPRIMER"} className="bg-[#0E2D43]" data-testid="demo-confirm-ok">Confirmer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
}

export default function Imports() {
  const specs = useFetch("/imports/specs");
  const log = useFetch("/imports");
  const sum = useFetch("/demo/summary");
  const onDone = () => {
    log.reload();
    sum.reload();
  };
  const [kind, setKind] = useState("vehicules_livraison");
  const list = specs.data || [];
  const idx = list.findIndex((s) => s.kind === kind);
  return (
    <div data-testid="imports-page">
      <PageHeader overline="Paramètres" title="Imports" />
      <div className="grid xl:grid-cols-[280px_1fr] gap-6">
        <nav className="space-y-1.5 min-w-0" data-testid="import-kinds">
          <div className="text-[11px] uppercase font-bold tracking-wider text-slate-500 mb-2 px-1">Ordre recommandé</div>
          {list.map((s, i) => (
            <button key={s.kind} onClick={() => setKind(s.kind)} data-testid={`import-kind-${s.kind}`}
              className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${kind === s.kind ? "bg-[#0E2D43] border-[#0E2D43] text-white" : "bg-white border-slate-200 text-[#0E2D43] hover:border-[#0E2D43]"}`}>
              <span className={`w-6 h-6 rounded-full grid place-items-center text-xs ${kind === s.kind ? "bg-[#F49619] text-[#0E2D43]" : "bg-slate-100"}`}>{i + 1}</span>
              <FileSpreadsheet className="w-4 h-4 shrink-0 opacity-60" />{s.label}
            </button>
          ))}
        </nav>
        <div className="min-w-0">
          {specs.loading ? <Loading /> : idx >= 0 && <ImportStep spec={list[idx]} step={idx + 1} onDone={onDone} />}
        </div>
      </div>
      <div className="grid xl:grid-cols-2 gap-6 mt-8">
        <DemoPanel sum={sum} />
        <Panel title="Journal des imports" flush testid="imports-log">
          {!log.data?.length ? <Empty text="Aucun import réalisé" /> : (
            <table className="tbl w-full"><thead><tr><th>Date</th><th>Type</th><th>Fichier</th><th className="text-right">Lignes</th><th>Par</th></tr></thead>
              <tbody>{log.data.map((l) => <tr key={l.id}><td>{fDate(l.at)}</td><td>{KIND[l.kind] || l.kind}</td><td className="max-w-[160px] truncate">{l.filename}</td><td className="text-right">{l.count}</td><td>{l.by}</td></tr>)}</tbody></table>
          )}
        </Panel>
      </div>
    </div>
  );
}
